import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import {
  Compass, Search, Globe, Mail, Loader2, CheckCircle2, AlertCircle,
  Plus, ExternalLink, Sparkles, MapPin
} from "lucide-react";
import GoogleMapsDiscover from "../components/GoogleMapsDiscover";

const NICHE_EXAMPLES = [
  "dentisti lodi", "ristoranti milano centro", "studi commercialisti torino",
  "palestre bologna", "parrucchieri roma trastevere", "agenzie immobiliari firenze",
  "veterinari brescia", "officine meccaniche modena",
];

export default function Scopri() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("brave"); // "brave" | "gmaps"
  const [health, setHealth] = useState(null);
  const [query, setQuery] = useState("");
  const [pages, setPages] = useState(3);  // 1 = quick (~80 lead max), 5 = deep (400+)
  const [expandZones, setExpandZones] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [lists, setLists] = useState([]);
  const [chosenListId, setChosenListId] = useState("");
  const [adding, setAdding] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [newListName, setNewListName] = useState("");

  useEffect(() => {
    api.get("/discover/health").then(r => setHealth(r.data)).catch(()=>{});
    api.get("/lists").then(r => setLists(r.data || [])).catch(()=>{});
  }, []);

  const reloadLists = async () => {
    const r = await api.get("/lists");
    setLists(r.data || []);
    return r.data || [];
  };

  const handleListChange = (val) => {
    if (val === "__NEW__") {
      setCreatingList(true);
      setChosenListId("");
    } else {
      setChosenListId(val);
    }
  };

  const createAndChooseList = async () => {
    const name = newListName.trim();
    if (!name) return;
    try {
      const { data: lst } = await api.post("/lists", { name });
      await reloadLists();
      setChosenListId(lst.id);
      setCreatingList(false);
      setNewListName("");
    } catch (e) {
      alert("Errore creazione lista: " + (e.response?.data?.detail || e.message));
    }
  };

  const [jobInfo, setJobInfo] = useState(null);  // {jobId, status, progress, estimated_seconds}

  const run = async (e) => {
    e?.preventDefault?.();
    if (!query.trim() || query.trim().length < 3) return;
    setLoading(true); setData(null); setSelected({}); setJobInfo(null);
    try {
      const { data } = await api.post("/discover/search", {
        query: query.trim(),
        maxQueries: 6,
        countPerQuery: 20,
        pages: pages,
        expandZones: expandZones,
        scrape: true,
        autoSave: true,
      });
      // Background job? Poll until complete.
      if (data?.jobId) {
        setJobInfo({ jobId: data.jobId, status: "queued",
                     estimated_seconds: data.estimated_seconds,
                     estimated_brave_calls: data.estimated_brave_calls });
        await pollJob(data.jobId);
      } else {
        setData(data);
      }
    } catch (err) {
      alert("Errore ricerca: " + (err.response?.data?.detail || err.message));
    } finally { setLoading(false); }
  };

  const pollJob = async (jobId) => {
    // Poll every 4s up to 8 minutes
    const deadline = Date.now() + 8 * 60 * 1000;
    while (Date.now() < deadline) {
      try {
        const { data: j } = await api.get(`/discover/job/${jobId}`);
        setJobInfo(prev => ({ ...(prev||{}), ...j }));
        if (j.status === "completed") { setData(j.result); return; }
        if (j.status === "error") { alert("Errore job: " + (j.error || "sconosciuto")); return; }
      } catch (e) {
        // Retry silently
      }
      await new Promise(r => setTimeout(r, 4000));
    }
    alert("Job in timeout. Riprova con profondità minore.");
  };

  const toggleAll = (val) => {
    if (!data) return;
    const next = {};
    if (val) data.results.forEach(r => { if (r.companyId) next[r.companyId] = true; });
    setSelected(next);
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]), [selected]);

  const addSelectedToList = async () => {
    if (!chosenListId || selectedIds.length === 0) return;
    setAdding(true);
    try {
      const { data: res } = await api.post(`/lists/${chosenListId}/items`, {
        companyIds: selectedIds,
      });
      alert(`✅ ${res.added} aziende aggiunte alla lista.`);
      setSelected({});
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally { setAdding(false); }
  };

  return (
    <div data-testid="scopri-page">
      <div className="flex items-start gap-3 mb-6">
        <Compass size={28} color="var(--mv-primary)"/>
        <div>
          <div className="h-display">Scopri lead</div>
          <div className="text-muted mt-1">
            Trova nuovi lead da motori di ricerca e mappe. Dedup automatico verso il CRM.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4" style={{borderBottom:"1px solid #eee"}}>
        <button onClick={()=>setTab("brave")} data-testid="tab-brave"
          style={{
            padding:"10px 18px",background:"transparent",border:"none",
            fontWeight:600,fontSize:14,cursor:"pointer",
            color: tab === "brave" ? "var(--mv-primary)" : "var(--mv-text-muted)",
            borderBottom: tab === "brave" ? "2px solid var(--mv-primary)" : "2px solid transparent",
            marginBottom:-1,display:"flex",alignItems:"center",gap:6,
          }}>
          <Globe size={15}/> Brave Search (web)
        </button>
        <button onClick={()=>setTab("gmaps")} data-testid="tab-gmaps"
          style={{
            padding:"10px 18px",background:"transparent",border:"none",
            fontWeight:600,fontSize:14,cursor:"pointer",
            color: tab === "gmaps" ? "var(--mv-primary)" : "var(--mv-text-muted)",
            borderBottom: tab === "gmaps" ? "2px solid var(--mv-primary)" : "2px solid transparent",
            marginBottom:-1,display:"flex",alignItems:"center",gap:6,
          }}>
          <MapPin size={15}/> Google Maps (attività locali)
        </button>
      </div>

      {tab === "gmaps" ? <GoogleMapsDiscover/> : (
      <>
      {/* Source health */}
      {health && !health.brave_configured && (
        <div className="card mb-4" style={{padding:14,borderLeft:"4px solid var(--mv-danger)",background:"#fef1f1"}}>
          <div className="flex gap-2 items-start">
            <AlertCircle size={18} color="var(--mv-danger)"/>
            <div className="text-small">
              <strong>BRAVE_SEARCH_API_KEY non configurata.</strong> Aggiungi la chiave in <code>backend/.env</code> per attivare la scoperta lead. (Free 2000 query/mese da <a href="https://api.search.brave.com/app/keys" target="_blank" rel="noreferrer">api.search.brave.com</a>)
            </div>
          </div>
        </div>
      )}

      {/* Search bar */}
      <form onSubmit={run} className="card mb-6" style={{padding:18}}>
        <label className="label">Cerca lead per nicchia e città</label>
        <div className="flex gap-2 mt-2">
          <div style={{flex:1, position:"relative"}}>
            <Search size={16} style={{position:"absolute",left:12,top:12,color:"var(--mv-text-muted)"}}/>
            <input
              data-testid="discover-query-input"
              value={query} onChange={e=>setQuery(e.target.value)}
              placeholder="es. dentisti lodi"
              className="input"
              style={{paddingLeft:34, fontSize:16}}
              disabled={loading}
            />
          </div>
          <button type="submit" disabled={loading || query.trim().length<3 || !health?.brave_configured}
            className="btn btn-primary" data-testid="discover-run-btn">
            {loading ? <><Loader2 size={16} className="spin"/> Ricerca in corso…</> : <><Compass size={16}/> Scopri</>}
          </button>
        </div>

        {/* Search depth controls */}
        <div className="flex gap-4 items-center mt-3" style={{flexWrap:"wrap"}}>
          <div className="flex gap-2 items-center" data-testid="depth-control">
            <label className="text-small" style={{fontWeight:600}}>Profondità ricerca:</label>
            <select className="input" style={{padding:"6px 10px",fontSize:13,width:"auto"}}
              value={pages} onChange={e=>setPages(Number(e.target.value))} disabled={loading}>
              <option value={1}>Veloce (~80 lead, 6 query)</option>
              <option value={3}>Standard (~200 lead, 18 query)</option>
              <option value={5}>Approfondita (~400 lead, 30 query)</option>
              <option value={10}>Massima (~800 lead, 60 query)</option>
            </select>
          </div>
          <label className="flex gap-2 items-center text-small" style={{cursor:"pointer"}} data-testid="zones-control">
            <input type="checkbox" checked={expandZones} onChange={e=>setExpandZones(e.target.checked)} disabled={loading}/>
            <span style={{fontWeight:600}}>Espandi zone città (Milano/Roma/Torino…)</span>
          </label>
        </div>

        <div className="text-small text-muted mt-3">
          <strong>Esempi:</strong> {NICHE_EXAMPLES.map((ex,i) => (
            <button key={ex} type="button" onClick={()=>setQuery(ex)}
              className="link" style={{background:"none",border:"none",cursor:"pointer",padding:"0 4px",color:"var(--mv-primary)"}}
              data-testid={`example-${i}`}>
              {ex}{i<NICHE_EXAMPLES.length-1?" ·":""}
            </button>
          ))}
        </div>
      </form>

      {loading && (
        <div className="card text-center" style={{padding:48}} data-testid="discover-loading">
          <Loader2 size={32} className="spin" style={{margin:"0 auto 12px",color:"var(--mv-primary)"}}/>
          <div>Sto cercando su Brave + estraggo email dai siti…</div>
          {jobInfo?.jobId ? (
            <div className="text-small text-muted mt-3">
              <div><strong>Job in background</strong> · ID: {jobInfo.jobId.slice(0,8)}</div>
              <div className="mt-2">Stato: <strong>{jobInfo.status}</strong></div>
              {jobInfo.progress && (
                <div className="mt-2">
                  Brave: {jobInfo.progress.brave_done || 0} / {jobInfo.estimated_brave_calls || "?"} ·
                  Domini trovati: {jobInfo.progress.total_domains || 0} ·
                  Scraped: {jobInfo.progress.scrape_done || 0}
                </div>
              )}
              <div className="mt-2" style={{fontSize:11,opacity:0.7}}>
                Tempo stimato: ~{Math.ceil((jobInfo.estimated_seconds || 60)/60)} min
              </div>
            </div>
          ) : (
            <div className="text-small text-muted mt-2">
              {pages === 1 ? "~10-20 secondi" : pages <= 3 ? "~30-60 secondi" : pages <= 5 ? "~1-2 minuti" : "~2-4 minuti"}
              {expandZones ? " (con zone città e scraping in parallelo)" : " (con scraping in parallelo)"}
            </div>
          )}
        </div>
      )}

      {data && !loading && (
      <>
        <div className="grid gap-3 mb-4" style={{gridTemplateColumns:"repeat(auto-fit, minmax(160px,1fr))"}}>
          <div className="card" style={{padding:14}}>
            <div className="stat-label">Risultati trovati</div>
            <div className="stat-num">{data.total_found}</div>
          </div>
          <div className="card" style={{padding:14}}>
            <div className="stat-label">Con email reale</div>
            <div className="stat-num" style={{color:"var(--mv-success)"}}>{data.with_email}</div>
            <div className="text-small text-muted mt-1">
              {data.total_found > 0 ? Math.round(data.with_email*100/data.total_found) : 0}% hit rate
            </div>
          </div>
          <div className="card" style={{padding:14}}>
            <div className="stat-label">Nuove nel CRM</div>
            <div className="stat-num">{data.new_companies}</div>
            <div className="text-small text-muted mt-1">{data.linked_existing} già esistenti</div>
          </div>
          <div className="card" style={{padding:14}}>
            <div className="stat-label">Query Brave usate</div>
            <div className="stat-num">{data.queries_used.length}</div>
            <div className="text-small text-muted mt-1">su 2.000/mese disponibili</div>
          </div>
        </div>

        <div className="card mb-4" style={{padding:14}}>
          <div className="text-small text-muted">Query lanciate:</div>
          <div className="flex gap-2 mt-1" style={{flexWrap:"wrap"}}>
            {data.queries_used.map(q => (
              <span key={q} className="badge" style={{background:"#f0f0f0",padding:"3px 8px",borderRadius:4,fontSize:12}}>{q}</span>
            ))}
          </div>
        </div>

        {data.results.length > 0 && (
          <div className="card" style={{padding:0}}>
            <div className="flex justify-between items-center" style={{padding:14,borderBottom:"1px solid #eee",flexWrap:"wrap",gap:8}}>
              <div>
                <input type="checkbox"
                  data-testid="select-all-discover"
                  checked={selectedIds.length > 0 && selectedIds.length === data.results.filter(r=>r.companyId).length}
                  onChange={e=>toggleAll(e.target.checked)}/>
                <span className="ml-2 text-small">{selectedIds.length} selezionate</span>
              </div>
              {selectedIds.length > 0 && (
                <div className="flex gap-2 items-center" style={{flexWrap:"wrap"}}>
                  {creatingList ? (
                    <>
                      <input
                        autoFocus value={newListName}
                        onChange={e=>setNewListName(e.target.value)}
                        onKeyDown={e=>{if(e.key==="Enter")createAndChooseList(); if(e.key==="Escape"){setCreatingList(false);setNewListName("");}}}
                        placeholder="Nome nuova lista (es. Dentisti Lodi 2026)"
                        data-testid="new-list-name-input"
                        style={{padding:"6px 10px",border:"1px solid var(--mv-primary)",borderRadius:6,minWidth:240}}/>
                      <button onClick={createAndChooseList} disabled={!newListName.trim()}
                        className="btn btn-primary btn-sm" data-testid="confirm-new-list-btn">
                        <Plus size={14}/> Crea
                      </button>
                      <button onClick={()=>{setCreatingList(false);setNewListName("");}}
                        className="btn btn-ghost btn-sm">Annulla</button>
                    </>
                  ) : (
                    <select value={chosenListId} onChange={e=>handleListChange(e.target.value)}
                      data-testid="select-target-list"
                      style={{padding:"6px 10px",border:"1px solid #ddd",borderRadius:6,minWidth:220}}>
                      <option value="">-- Scegli lista di destinazione --</option>
                      <option value="__NEW__" style={{fontWeight:600,color:"var(--mv-primary)"}}>+ Crea nuova lista…</option>
                      {lists.length > 0 && <option disabled>──────────────</option>}
                      {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.totalItems || 0})</option>)}
                    </select>
                  )}
                  <button onClick={addSelectedToList} disabled={!chosenListId || adding || creatingList}
                    className="btn btn-primary btn-sm" data-testid="add-to-list-btn">
                    {adding ? <Loader2 size={14} className="spin"/> : <Plus size={14}/>}
                    Aggiungi {selectedIds.length} alla lista
                  </button>
                </div>
              )}
            </div>
            <div style={{overflowX:"auto"}}>
              <table className="w-full" style={{borderCollapse:"collapse",fontSize:14}}>
                <thead>
                  <tr style={{background:"#fafafa",fontSize:12,textTransform:"uppercase",color:"var(--mv-text-muted)"}}>
                    <th style={{padding:"10px 8px",width:30}}></th>
                    <th style={{textAlign:"left",padding:"10px 8px"}}>Nome</th>
                    <th style={{textAlign:"left",padding:"10px 8px"}}>Sito</th>
                    <th style={{textAlign:"left",padding:"10px 8px"}}>Email primaria</th>
                    <th style={{textAlign:"right",padding:"10px 8px"}}>Altre email</th>
                    <th style={{padding:"10px 8px"}}></th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r) => (
                    <tr key={r.companyId || r.website} data-testid={`result-${r.website}`}
                      style={{borderTop:"1px solid #f0f0f0"}}>
                      <td style={{padding:"10px 8px"}}>
                        {r.companyId && (
                          <input type="checkbox"
                            checked={!!selected[r.companyId]}
                            onChange={e=>setSelected({...selected, [r.companyId]: e.target.checked})}/>
                        )}
                      </td>
                      <td style={{padding:"10px 8px",fontWeight:600}}>
                        {r.companyName}
                        <div className="text-small text-muted" style={{marginTop:2,fontWeight:400}}>{r.found_via_query}</div>
                      </td>
                      <td style={{padding:"10px 8px"}}>
                        <a href={`https://${r.website}`} target="_blank" rel="noreferrer"
                          className="link" style={{color:"var(--mv-primary)"}}>
                          {r.website} <ExternalLink size={11} style={{display:"inline"}}/>
                        </a>
                      </td>
                      <td style={{padding:"10px 8px"}}>
                        {r.best_email ? (
                          <span style={{color:"var(--mv-success)",fontWeight:600}}>
                            <CheckCircle2 size={12} style={{display:"inline",marginRight:4}}/>
                            {r.best_email}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td style={{padding:"10px 8px",textAlign:"right"}}>
                        {r.emails.length > 1 ? <span className="text-muted">+{r.emails.length - 1}</span> : ""}
                      </td>
                      <td style={{padding:"10px 8px"}}>
                        {r.companyId && (
                          <button onClick={()=>navigate(`/crm`)} className="btn btn-ghost btn-sm">
                            Apri →
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {data.results.length === 0 && (
          <div className="card text-center" style={{padding:40}} data-testid="discover-no-results">
            <Sparkles size={32} color="var(--mv-text-muted)" style={{margin:"0 auto 12px"}}/>
            <div>Nessun risultato. Prova una query più specifica (es. aggiungi la città).</div>
          </div>
        )}
      </>
      )}
      </>
      )}
    </div>
  );
}
